import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  getOrganizations,
  getOrganizationById,
  createOrganization,
  updateOrganization,
  getOrgIntegrations,
  createOrgIntegration,
  updateOrgIntegration,
  getOrgMembers,
  addUserToOrg,
  removeUserFromOrg,
  setUserDefaultOrg,
  unsetUserDefaultOrg,
  updateMemberRole,
  updateOrgMetadata,
  updateOrgSettings,
} from '../api/organizations.api.ts';
import { adminQueryKeys } from './queryKeys.ts';
import { getErrorMessage } from '../utils/errorUtils.ts';
import type {
  Organization,
  OrganizationMember,
  Integration,
  ApiMessageResponse,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  AddUserToOrgRequest,
  UpdateMemberRoleRequest,
  UpdateOrgMetadataRequest,
  UpdateOrgSettingsRequest,
} from '../types.ts';

export const useOrganizations = (
  options?: { enabled?: boolean },
): UseQueryResult<Organization[], Error> => {
  return useQuery({
    queryKey: adminQueryKeys.organizations.lists(),
    queryFn: getOrganizations,
    staleTime: 5 * 60 * 1000, // 5 min - avoid duplicate fetches, single call via cache
    enabled: options?.enabled !== false,
  });
};

export const useOrganization = (id: string | undefined): UseQueryResult<Organization, Error> => {
  return useQuery({
    queryKey: adminQueryKeys.organizations.detail(id ?? ''),
    queryFn: () => getOrganizationById(id!),
    enabled: !!id,
  });
};

export const useCreateOrganization = (): UseMutationResult<Organization, Error, CreateOrganizationRequest> => {
  const queryClient = useQueryClient();
  return useMutation<Organization, Error, CreateOrganizationRequest>({
    mutationFn: (payload: CreateOrganizationRequest) => createOrganization(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.lists() });
      toast.success('Organization created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create organization'));
    },
  });
};

export const useUpdateOrganization = (orgId: string): UseMutationResult<Organization, Error, UpdateOrganizationRequest> => {
  const queryClient = useQueryClient();
  return useMutation<Organization, Error, UpdateOrganizationRequest>({
    mutationFn: (payload: UpdateOrganizationRequest) =>
      updateOrganization(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.lists() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.detail(orgId) });
      toast.success('Organization updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update organization'));
    },
  });
};

export const useOrgIntegrations = (orgId: string | undefined): UseQueryResult<Integration[], Error> => {
  return useQuery({
    queryKey: adminQueryKeys.organizations.integrations(orgId ?? ''),
    queryFn: () => getOrgIntegrations(orgId!),
    enabled: !!orgId,
  });
};

export const useCreateOrgIntegration = (orgId: string): UseMutationResult<{ data: Integration }, Error, CreateIntegrationRequest> => {
  const queryClient = useQueryClient();
  return useMutation<{ data: Integration }, Error, CreateIntegrationRequest>({
    mutationFn: (payload: CreateIntegrationRequest) =>
      createOrgIntegration(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.integrations(orgId) });
      toast.success('Integration created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create integration'));
    },
  });
};

export const useUpdateOrgIntegration = (orgId: string): UseMutationResult<Integration, Error, { integrationId: string; payload: UpdateIntegrationRequest }> => {
  const queryClient = useQueryClient();
  return useMutation<Integration, Error, { integrationId: string; payload: UpdateIntegrationRequest }>({
    mutationFn: ({
      integrationId,
      payload,
    }: {
      integrationId: string;
      payload: UpdateIntegrationRequest;
    }) => updateOrgIntegration(orgId, integrationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.integrations(orgId) });
      toast.success('Integration updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update integration'));
    },
  });
};

export const useOrgMembers = (orgId: string | undefined): UseQueryResult<OrganizationMember[], Error> => {
  return useQuery({
    queryKey: adminQueryKeys.organizations.members(orgId ?? ''),
    queryFn: () => getOrgMembers(orgId!),
    enabled: !!orgId,
  });
};

export const useAddUserToOrg = (orgId: string): UseMutationResult<OrganizationMember, Error, AddUserToOrgRequest> => {
  const queryClient = useQueryClient();
  return useMutation<OrganizationMember, Error, AddUserToOrgRequest>({
    mutationFn: (payload: AddUserToOrgRequest) => addUserToOrg(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(orgId) });
      toast.success('User added to organization');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to add user'));
    },
  });
};

export const useRemoveUserFromOrg = (orgId: string): UseMutationResult<ApiMessageResponse, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation<ApiMessageResponse, Error, string>({
    mutationFn: (userId: string) => removeUserFromOrg(orgId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(orgId) });
      toast.success('User removed from organization');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove user'));
    },
  });
};

export const useSetUserDefaultOrg = (orgId: string): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId: string) => setUserDefaultOrg(orgId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(orgId) });
      toast.success('Default organization updated');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to set default organization'));
    },
  });
};

export const useUnsetUserDefaultOrg = (orgId: string): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (userId: string) => unsetUserDefaultOrg(orgId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(orgId) });
      toast.success('Default organization removed');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove default organization'));
    },
  });
};

export const useUpdateMemberRole = (orgId: string): UseMutationResult<OrganizationMember, Error, { userId: string; payload: UpdateMemberRoleRequest }> => {
  const queryClient = useQueryClient();
  return useMutation<OrganizationMember, Error, { userId: string; payload: UpdateMemberRoleRequest }>({
    mutationFn: ({
      userId,
      payload,
    }: {
      userId: string;
      payload: UpdateMemberRoleRequest;
    }) => updateMemberRole(orgId, userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(orgId) });
      toast.success('Member role updated');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update role'));
    },
  });
};

export const useUpdateOrgMetadata = (orgId: string): UseMutationResult<Organization, Error, UpdateOrgMetadataRequest> => {
  const queryClient = useQueryClient();
  return useMutation<Organization, Error, UpdateOrgMetadataRequest>({
    mutationFn: (payload: UpdateOrgMetadataRequest) =>
      updateOrgMetadata(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.detail(orgId) });
      toast.success('Metadata updated');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update metadata'));
    },
  });
};

export const useUpdateOrgSettings = (orgId: string): UseMutationResult<Organization, Error, UpdateOrgSettingsRequest> => {
  const queryClient = useQueryClient();
  return useMutation<Organization, Error, UpdateOrgSettingsRequest>({
    mutationFn: (payload: UpdateOrgSettingsRequest) =>
      updateOrgSettings(orgId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.detail(orgId) });
      toast.success('Settings updated');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update settings'));
    },
  });
};
